import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import { SentenceStore , AssignableConceptStore } from '@seedlang/stores';
import { isPresent, isBlank } from '@seedlang/utils';
import Spinner from 'components/spinner';
import { keys } from 'lodash';
import styled from '@emotion/styled';
import { AppUI } from '@seedlang/state';
import { Constants, Theme } from '@seedlang/constants';
import ButtonPrimary from 'components/button/button_primary';
import Alert from 'components/alert';

const Wrapper = styled.div`
  .sentence-create {
    .spinner {
      margin-bottom: 20px;
    }
    textarea {
      width: 100%;
      margin-bottom: 10px;
      height: 34px;
      font-size: 14px;
      line-height: 16px;
    }
    .targetText {
      margin-bottom: 0;
    }
    .characters-remaining {
      font-size: 11px;
      margin-bottom: 10px;
      line-height: 15px;
    }
    .lookup-grammar {
      margin-bottom: 5px;
    }
  }
`;

const Button = styled.div`
  cursor: pointer;
  padding: 10px;
  font-size: 12px;
  background: ${props => props.background};
  color: ${props => props.color};
  margin: 0 10px 10px 0;
  border-radius: 10px;
`;

const ExpandTriviaLink = styled.div`
  text-decoration: underline;
  margin: 0 0 10px 3px;
  cursor: pointer;
  font-size: 14px;
`;

@observer
class SentenceCreate extends React.Component {
  constructor(props) {
    super(props);
    this.answerSourceTextRef = React.createRef();
    this.answerTargetTextRef = React.createRef();
    this.sourceTextRef = React.createRef();
    this.targetTextRef = React.createRef();
  }

  @observable targetText = this.props.targetText || '';
  @observable sourceText = this.props.sourceText || '';
  @observable answerTargetText = '';
  @observable answerSourceText = '';
  @observable conceptId = this.props.conceptId;
  @observable showSpinner = false;
  @observable showNumberWarning = false;
  @observable showSentenceCountWarning = false;
  @observable showQuotationMarkSpaceWarning = false;
  @observable showTriviaOptionInputs = this.props.showTriviaOptionInputs;
  @observable triviaAnswer1 = '';
  @observable triviaAnswer2 = '';
  @observable triviaAnswer3 = '';
  @observable triviaAnswer4 = '';
  @observable languageId;

  @computed get characterCount() {
    return this.targetText.length;
  }

  componentDidMount() {
    AssignableConceptStore.getIndex({limit: 9999});
    this.languageId = keys(Constants.LANGUAGE_OPTIONS).includes(AppUI.user.targetLanguageId) ? AppUI.user.targetLanguageId : 'DE';
  }

  updateTargetText = () => {
    const targetText = this.targetTextRef.current.value.replace(/\.{3}/g, Constants.SPECIAL_PUNCTUATION.ELLIPSIS);
    const pattern = new RegExp(`([.?!]|.+${Constants.SPECIAL_PUNCTUATION.ELLIPSIS})\\s*\\S`);
    if (pattern.test(targetText)) {
      this.showSentenceCountWarning = true;
      this.targetText = this.targetTextRef.current.value;
    } else if (this.targetTextRef.current.value.match(/\d+/)) {
      this.showNumberWarning = true;
      this.targetText = this.targetTextRef.current.value;
    } else if (this.targetTextRef.current.value.match(/(«\s)|(\s»)/)) {
      this.showQuotationMarkSpaceWarning = true;
      this.targetText = this.targetTextRef.current.value;
    } else {
      this.showNumberWarning = false;
      this.showSentenceCountWarning = false;
      this.showQuotationMarkSpaceWarning = false;
      this.targetText = this.targetTextRef.current.value;
    }
  }

  onChange = () => {
    this.characterCount = this.targetTextRef.current.value.length;
  }

  onClick = () => {
    if (isPresent(this.targetTextRef.current.value) && isPresent(this.sourceTextRef.current.value)) {
      this.showSpinner = true;
      SentenceStore.create(
        {
          data: {
            targetText: this.targetText,
            sourceText: this.sourceText,
            answerTargetText: this.answerTargetText,
            answerSourceText: this.answerSourceText,
            interjectionId: this.props.interjectionId,
            questionId: this.props.questionId,
            conceptIds: isPresent(this.conceptId) ? [this.conceptId] : null,
            timestamp: '',
            triviaAnswer1: this.triviaAnswer1,
            triviaAnswer2: this.triviaAnswer2,
            triviaAnswer3: this.triviaAnswer3,
            triviaAnswer4: this.triviaAnswer4,
            submittedBy: this.props.submittedBy,
            triviaWordId: this.props.triviaWordId,
            languageId: this.languageId,
          },
        },
        this.afterCreate,
      );
      this.targetText = '';
      this.sourceText = '';
      this.answerTargetText = '';
      this.answerSourceText = '';
      this.triviaAnswer1 = '';
      this.triviaAnswer2 = '';
      this.triviaAnswer3 = '';
      this.triviaAnswer4 = '';
    }
  }

  afterCreate = (resp) => {
    this.showSpinner = false;
    if (this.props.afterCreate) {
      this.props.afterCreate(resp);
    }
  }

  onEnterPressClick = (e) => {
    if (e.key === 'Enter') {
      return this.onClick();
    }
  }

  render() {
    return (
      <Wrapper>
        <div className='sentence-create translation-create'>
          {this.showSpinner && <Spinner className='blue' />}
          <div>
            {
              keys(Constants.LANGUAGE_OPTIONS).map(languageId => {
                return (
                  <Button
                    onClick={() => this.languageId = languageId}
                    background={this.languageId === languageId ? Theme.green : '#CCC'}
                    color={this.languageId === languageId ? '#FFF' : '#333'}
                  >
                    {Constants.LANGUAGE_OPTIONS[languageId]}
                  </Button>
                );
              })
            }
          </div>
          <div>
              <input
                className='targetText'
                type='text'
                maxLength='110'
                placeholder={this.showTriviaOptionInputs ? 'Enter a Trivia Question' : 'Enter a Sentence'}
                onChange={() => this.updateTargetText()}
                ref={this.targetTextRef}
                value={this.targetText}
              />
              <div className='characters-remaining'>
                Characters remaining: {110 - this.characterCount}
              </div>
              {
                this.showNumberWarning &&
                  <Alert margin='0 0 10px 0'>
                    Numbers should be spelled out whenever possible, ex '100' should be 'hundert'.
                  </Alert>
              }
              {
                this.showSentenceCountWarning &&
                  <Alert margin='0 0 10px 0'>
                    Only one sentence should be added at a time.
                  </Alert>
              }
              {
                this.showQuotationMarkSpaceWarning &&
                  <Alert margin='0 0 10px 0'>
                    Please do not include spaces just after or before quotation marks.
                  </Alert>
              }
          </div>
          <div>
              <input
                type='text'
                placeholder='Enter an English Translation'
                value={this.sourceText}
                onChange={() => this.sourceText = this.sourceTextRef.current.value}
                ref={this.sourceTextRef}
                onKeyPress={this.onEnterPressClick}
              />
          </div>
          {
            !this.props.simple && !this.showTriviaOptionInputs &&
              <ExpandTriviaLink
                onClick={() => this.showTriviaOptionInputs = true}
              >
                Show Trivia Fields
              </ExpandTriviaLink>
          }
          {
            !this.props.simple && this.showTriviaOptionInputs &&
              <div style={{marginTop: '10px'}}>
                <div style={{marginBottom: 10}}>
                  <div style={{paddingRight: 10, display: 'inline-block', width: '50%'}}>
                    <input
                      value={this.triviaAnswer1}
                      onChange={el => this.triviaAnswer1 = el.target.value}
                      placeholder='Correct Option'
                    />
                  </div>
                  <div style={{display: 'inline-block', width: '50%'}}>
                    <input
                      value={this.triviaAnswer2}
                      onChange={el => this.triviaAnswer2 = el.target.value}
                      placeholder='Incorrect Option 1'
                    />
                  </div>
                </div>
                <div style={{marginBottom: 10}}>
                  <div style={{paddingRight: 10, display: 'inline-block', width: '50%'}}>
                    <input
                      value={this.triviaAnswer3}
                      onChange={el => this.triviaAnswer3 = el.target.value}
                      placeholder='Incorrect Option 2'
                    />
                  </div>
                  <div style={{display: 'inline-block', width: '50%'}}>
                    <input
                      value={this.triviaAnswer4}
                      onChange={el => this.triviaAnswer4 = el.target.value}
                      placeholder='Incorrect Option 3'
                    />
                  </div>
                </div>
                <div style={{marginBottom: 10}}>
                    <input
                      type='text'
                      maxLength='110'
                      placeholder='Enter a Trivia Answer'
                      onChange={() => this.answerTargetText = this.answerTargetTextRef.current.value}
                      ref={this.answerTargetTextRef}
                      value={this.answerTargetText}
                    />
                </div>
                <div style={{marginBottom: 10}}>
                    <input
                      type='text'
                      maxLength='110'
                      placeholder='Enter an English Translation'
                      onChange={() => this.answerSourceText = this.answerSourceTextRef.current.value}
                      ref={this.answerSourceTextRef}
                      value={this.answerSourceText}
                    />
                </div>
              </div>
          }
          <div>
              <ButtonPrimary
                disabled={isBlank(this.targetText)}
                onClick={this.onClick}
              >
                Create
              </ButtonPrimary>
          </div>
        </div>
      </Wrapper>
    );
  }
}

export default SentenceCreate;
